import { useEffect, useMemo, useRef, useState } from "react";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  ArrowLeft,
  ArrowRight,
  Ban,
  Building2,
  Check,
  Eye,
  EyeOff,
  Loader2,
  Users,
  X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/cn";
import { useDrawerA11y } from "@/hooks/useDrawerA11y";
import { useIsMobile } from "@/hooks/useIsMobile";
import { useAccessPreview, useCreateUser } from "@/hooks/useAdminUsers";
import { ManagerSyncWizardPanel } from "@/components/admin/ManagerSyncSection";
import { RoleAssignmentFields } from "@/components/admin/RoleAssignmentFields";
import type { AdminUsersOptions, RoleConfig } from "@/api/adminCenter";

/**
 * D-044 S1 — #2 Add-user wizard (VISUAL v1.1: 6 кроків, MP6-MP10).
 * Drawer (desktop) / fullscreen (mobile) — MP10. Canonical-форма (D-031).
 * Preview = backend resolver-compute on-enter ⑥ (AC-ADM-012/014, MP9).
 * NULL=DENY: без tier save заблоковано з видимою причиною (AC-ADM-011).
 */

const STEPS = ["Профіль", "Рівень доступу", "Функція", "Права", "Команда", "Перевірка"];

const profileSchema = z.object({
  name: z.string().trim().min(2, "Вкажіть імʼя"),
  email: z.string().trim().email("Невалідна електронна пошта"),
  phone: z
    .string()
    .trim()
    .regex(/^(\+380\d{9})?$/, "Формат: +380XXXXXXXXX")
    .optional()
    .or(z.literal("")),
});

type ProfileForm = z.infer<typeof profileSchema>;

export interface AddUserWizardProps {
  open: boolean;
  options: AdminUsersOptions;
  onClose: () => void;
  onCreated: (id: string, name: string) => void;
}

export function AddUserWizard({ open, options, onClose, onCreated }: AddUserWizardProps) {
  const isMobile = useIsMobile();
  const panelRef = useDrawerA11y(open, onClose);

  const [step, setStep] = useState(0);
  const [cfg, setCfg] = useState<RoleConfig>({
    tier: null,
    functional: null,
    capabilities: [],
    teamId: null,
    adminConfirmed: false,
  });
  const [serverError, setServerError] = useState<string | null>(null);
  // D-048: post-create стан — wizard НЕ закривається одразу, а показує статус
  // manager-scoped BI-синхронізації (MP3, читає CManagerScopedSyncRun).
  const [created, setCreated] = useState<{ id: string; name: string } | null>(null);

  const form = useForm<ProfileForm>({
    resolver: zodResolver(profileSchema),
    defaultValues: { name: "", email: "", phone: "" },
    mode: "onBlur",
  });

  // MP10: compute on-enter ⑥ (+ recalc-on-back, бо query key = cfg)
  const previewCfg = useMemo(() => (step === 5 ? cfg : null), [step, cfg]);
  const previewQ = useAccessPreview(previewCfg);

  const createM = useCreateUser();

  // S1-hotfix: компонент лишається змонтованим (open=false → null), тож стан
  // персистить між відкриттями. Повний reset при КОЖНОМУ відкритті — щоб не
  // лишалось залишків попереднього створення (step/cfg/rhf/error).
  const wasOpen = useRef(false);
  useEffect(() => {
    if (open && !wasOpen.current) {
      setStep(0);
      setCfg({ tier: null, functional: null, capabilities: [], teamId: null, adminConfirmed: false });
      setServerError(null);
      setCreated(null);
      form.reset({ name: "", email: "", phone: "" });
    }
    wasOpen.current = open;
  }, [open, form]);

  if (!open) return null;

  // D-048 wizard-фінал: користувача створено (tier=manager) → статус
  // BI-синхронізації з полінгом замість миттєвого закриття.
  if (created) {
    return (
      <div className="fixed inset-0 z-40">
        <div className="absolute inset-0 bg-black/40" aria-hidden />
        <div
          ref={panelRef}
          role="dialog"
          aria-modal="true"
          aria-label="Користувача створено"
          className={cn(
            "absolute bg-card shadow-xl outline-none",
            isMobile
              ? "inset-0 overflow-y-auto p-4"
              : "right-0 top-0 h-full w-[480px] overflow-y-auto border-l p-5",
          )}
        >
          <div className="mb-4 flex items-center justify-between">
            <h2 className="text-base font-semibold">Користувача створено</h2>
            <Button
              variant="ghost"
              size="sm"
              aria-label="Закрити"
              onClick={() => onCreated(created.id, created.name)}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
          <div className="mb-3 flex items-center gap-2 rounded-lg bg-success px-3 py-2.5 text-sm text-success-foreground">
            <Check className="h-4 w-4" /> {created.name} — обліковий запис створено.
          </div>
          <ManagerSyncWizardPanel userId={created.id} />
          <div className="mt-4 border-t pt-3.5 text-right">
            <Button size="sm" onClick={() => onCreated(created.id, created.name)}>
              Готово
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const isSales = cfg.tier === "manager" || cfg.tier === "supervisor";

  // NULL=DENY reasons (видимі, не сірий-мовчазний — §0)
  const saveBlockReason =
    cfg.tier === null
      ? "Оберіть Рівень доступу — без нього доступ = заборонено (NULL=DENY)"
      : cfg.tier === "admin" && !cfg.adminConfirmed
        ? "Підтвердіть призначення адміністратора"
        : null;

  const stepBlockReason =
    step === 1 && cfg.tier === "admin" && !cfg.adminConfirmed
      ? "Підтвердіть призначення адміністратора"
      : null;

  const next = async () => {
    setServerError(null);
    if (step === 0) {
      const ok = await form.trigger();
      if (!ok) return;
    }
    if (step < 5) {
      setStep(step + 1);
      return;
    }
    // save
    if (saveBlockReason) return;
    const profile = form.getValues();
    createM.mutate(
      {
        name: profile.name.trim(),
        email: profile.email.trim(),
        phone: profile.phone?.trim() || undefined,
        ...cfg,
      },
      {
        // D-048: для tier=manager лишаємось у wizard і показуємо статус
        // підтягування клієнтів; для інших tier — поведінка як раніше.
        onSuccess: (res) => {
          if (cfg.tier === "manager") {
            setCreated({ id: res.id, name: profile.name.trim() });
          } else {
            onCreated(res.id, profile.name.trim());
          }
        },
        onError: (e) => setServerError(e instanceof Error ? e.message : "Помилка створення"),
      },
    );
  };

  return (
    <div className="fixed inset-0 z-40">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} aria-hidden />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label="Новий користувач"
        className={cn(
          "absolute bg-card shadow-xl outline-none",
          isMobile
            ? "inset-0 overflow-y-auto p-4"
            : "right-0 top-0 h-full w-[480px] overflow-y-auto border-l p-5",
        )}
      >
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-base font-semibold">Новий користувач</h2>
          <Button variant="ghost" size="sm" aria-label="Закрити" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </div>

        {/* Stepper */}
        <ol className="mb-5 flex flex-wrap gap-x-3 gap-y-1.5" aria-label="Кроки">
          {STEPS.map((s, i) => (
            <li key={s} className="flex items-center gap-1.5 text-xs">
              <span
                className={cn(
                  "flex h-5 w-5 items-center justify-center rounded-full border text-[10px]",
                  i < step && "border-transparent bg-success text-success-foreground",
                  i === step && "border-transparent bg-primary text-primary-foreground",
                  i > step && "text-muted-foreground",
                )}
              >
                {i < step ? <Check className="h-3 w-3" /> : i + 1}
              </span>
              <span className={i === step ? "text-foreground" : "text-muted-foreground"}>{s}</span>
            </li>
          ))}
        </ol>

        <div className="min-h-[260px]">
          {step === 0 && (
            <div className="space-y-3.5">
              <Field label="Імʼя" error={form.formState.errors.name?.message}>
                <Input {...form.register("name")} placeholder="Імʼя Прізвище" />
              </Field>
              <Field label="Електронна пошта" error={form.formState.errors.email?.message}>
                <Input {...form.register("email")} placeholder="email@kormaxtrade.com.ua" />
              </Field>
              <Field label="Телефон (опц.)" error={form.formState.errors.phone?.message}>
                <Input {...form.register("phone")} placeholder="+380XXXXXXXXX" />
              </Field>
            </div>
          )}

          {step === 1 && (
            <RoleAssignmentFields value={cfg} onChange={setCfg} options={options} sections={["tier"]} />
          )}
          {step === 2 && (
            <RoleAssignmentFields value={cfg} onChange={setCfg} options={options} sections={["functional"]} />
          )}
          {step === 3 && (
            <RoleAssignmentFields value={cfg} onChange={setCfg} options={options} sections={["capabilities"]} />
          )}

          {step === 4 && (
            <div className="space-y-3">
              {/* MP8: Sales team vs Функціональний відділ розведено */}
              {isSales && (
                <div className="rounded-lg bg-secondary p-3">
                  <div className="mb-2 flex items-center gap-1.5 text-sm font-medium">
                    <Users className="h-4 w-4" /> Команда продажів (Sales team)
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {options.teams.map((t) => (
                      <button
                        key={t.id}
                        type="button"
                        aria-pressed={cfg.teamId === t.id}
                        onClick={() =>
                          setCfg({ ...cfg, teamId: cfg.teamId === t.id ? null : t.id })
                        }
                        className={cn(
                          "rounded-badge px-2.5 py-1 text-xs",
                          cfg.teamId === t.id
                            ? "bg-info text-info-foreground"
                            : "bg-muted text-muted-foreground hover:bg-accent hover:text-foreground",
                        )}
                      >
                        {t.name}
                        {t.departmentName ? ` → ${t.departmentName}` : ""}
                      </button>
                    ))}
                  </div>
                  {cfg.tier === "supervisor" && !cfg.teamId && (
                    <p className="mt-2 rounded bg-warning px-2 py-1.5 text-xs text-warning-foreground">
                      Керівник без команди не бачитиме клієнтів (team-scope = NULL=DENY).
                    </p>
                  )}
                </div>
              )}
              {cfg.functional && (
                <div className="rounded-lg bg-secondary p-3">
                  <div className="mb-1 flex items-center gap-1.5 text-sm font-medium">
                    <Building2 className="h-4 w-4" /> Функціональний відділ
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {options.functional.find((f) => f.key === cfg.functional)?.label} — команда
                    продажів не обовʼязкова. Черга відділу — після додавання у відповідальні
                    («Відділи та маршрути»).
                  </p>
                </div>
              )}
              {!isSales && !cfg.functional && (
                <p className="text-xs text-muted-foreground">
                  Для рівнів Директор/Адмін команда не обовʼязкова.
                </p>
              )}
              <p className="text-xs text-muted-foreground">Команда визначає supervisor-scope.</p>
            </div>
          )}

          {step === 5 && (
            <div>
              <div className="mb-2.5 text-xs text-muted-foreground">
                Перевірка доступу — що користувач побачить після створення
              </div>
              {previewQ.isLoading && (
                <div className="flex items-center gap-2 py-8 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" /> Обчислення доступу (resolver)…
                </div>
              )}
              {previewQ.isError && (
                <div className="rounded-lg bg-destructive-soft px-3 py-2 text-sm text-destructive">
                  Не вдалося обчислити preview.
                </div>
              )}
              {previewQ.data && (
                <div className="grid gap-2.5 sm:grid-cols-2">
                  <div className="rounded-lg bg-success p-3">
                    <div className="mb-1.5 flex items-center gap-1.5 text-sm font-medium text-success-foreground">
                      <Eye className="h-4 w-4" /> Бачитиме
                    </div>
                    {previewQ.data.see.map((r) => (
                      <div key={r.key} className="flex items-start gap-1.5 py-0.5 text-xs leading-relaxed">
                        <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-success-foreground" />
                        {r.label}
                      </div>
                    ))}
                  </div>
                  <div className="rounded-lg bg-muted p-3">
                    <div className="mb-1.5 flex items-center gap-1.5 text-sm font-medium text-muted-foreground">
                      <EyeOff className="h-4 w-4" /> Не бачитиме
                    </div>
                    {previewQ.data.notSee.map((r) => (
                      <div key={r.key} className="flex items-start gap-1.5 py-0.5 text-xs leading-relaxed">
                        <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-muted-foreground" />
                        {r.label}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {serverError && (
                <div
                  role="alert"
                  className="mt-3 rounded-lg bg-destructive-soft px-3 py-2 text-sm text-destructive"
                >
                  {serverError}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="mt-4 flex items-center justify-between gap-3 border-t pt-3.5">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setStep(Math.max(0, step - 1))}
            className={step === 0 ? "invisible" : ""}
          >
            <ArrowLeft className="mr-1 h-4 w-4" /> Назад
          </Button>
          <div className="flex items-center gap-2.5">
            {(step === 5 && saveBlockReason) || stepBlockReason ? (
              <span className="flex items-center gap-1 rounded-badge bg-destructive-soft px-2 py-1 text-xs text-destructive">
                <Ban className="h-3 w-3" /> {step === 5 ? saveBlockReason : stepBlockReason}
              </span>
            ) : null}
            <Button
              size="sm"
              onClick={next}
              disabled={createM.isPending || (step === 5 && !!saveBlockReason) || !!stepBlockReason}
            >
              {createM.isPending ? (
                <Loader2 className="mr-1 h-4 w-4 animate-spin" />
              ) : step === 5 ? (
                <Check className="mr-1 h-4 w-4" />
              ) : null}
              {step === 5 ? "Створити користувача" : "Далі"}
              {step < 5 && <ArrowRight className="ml-1 h-4 w-4" />}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({
  label,
  error,
  children,
}: {
  label: string;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs text-muted-foreground">{label}</span>
      {children}
      {error && (
        <span role="alert" className="mt-1 block text-xs text-destructive">
          {error}
        </span>
      )}
    </label>
  );
}
